import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';
import { useSeoSchema } from '@/hooks/useSeoSchema';
import { treatmentServices } from '@/mocks/treatmentServices';
import { cities } from '@/mocks/cities';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://healthtravelturkiye.com';

type InstitutionRow = {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  district: string;
  description: string;
  type: string;
  category?: string;
  website?: string;
  logo_url?: string;
  cover_image_url?: string;
  is_approved: boolean;
  is_published: boolean;
  is_pro_member: boolean;
  status: string;
  created_at: string;
  services?: string[];
  departments?: string[];
};

type DoctorRow = {
  id: string;
  institution_id: string;
  name: string;
  title: string;
  specialization: string;
  experience_years: number;
  education: string;
  photo_url?: string;
};

export default function InstitutionsPage() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || '');
  const [selectedBranch, setSelectedBranch] = useState(searchParams.get('branch') || '');
  const [selectedServiceId, setSelectedServiceId] = useState(searchParams.get('service') || '');
  const [sortBy, setSortBy] = useState('created_at');

  // Resolve service id → serviceKeys for filtering
  const activeServiceKeys = useMemo(() => {
    if (!selectedServiceId) return null;
    const found = treatmentServices.find((s) => s.id === selectedServiceId);
    return found ? found.serviceKeys : null;
  }, [selectedServiceId]);
  const [institutions, setInstitutions] = useState<InstitutionRow[]>([]);
  const [doctorsMap, setDoctorsMap] = useState<Record<string, DoctorRow[]>>({});
  const [loading, setLoading] = useState(true);

  const [branchDropdownOpen, setBranchDropdownOpen] = useState(false);
  const [branchSearch, setBranchSearch] = useState('');
  const branchRef = useRef<HTMLDivElement>(null);

  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  const cityRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (branchRef.current && !branchRef.current.contains(e.target as Node)) {
        setBranchDropdownOpen(false);
        setBranchSearch('');
      }
      if (cityRef.current && !cityRef.current.contains(e.target as Node)) {
        setCityDropdownOpen(false);
        setCitySearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    const branch = searchParams.get('branch') || '';
    const q = searchParams.get('q') || '';
    const city = searchParams.get('city') || '';
    const service = searchParams.get('service') || '';
    setSelectedBranch(branch);
    setSearchQuery(q);
    setSelectedCity(city);
    setSelectedServiceId(service);
  }, [searchParams]);

  const loadInstitutions = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('institutions')
        .select('*')
        .eq('is_approved', true)
        .eq('is_published', true)
        .eq('status', 'active')
        .is('deleted_at', null)
        .order('is_pro_member', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) {
        setInstitutions([]);
      } else {
        const valid = (data || []).filter(
          (inst: any) =>
            inst.is_approved === true &&
            inst.is_published === true &&
            inst.status === 'active' &&
            inst.type !== 'agency' &&
            inst.deleted_at === null,
        );
        setInstitutions(valid as InstitutionRow[]);

        if (valid.length > 0) {
          const results = await Promise.all(
            valid.map(async (inst: any) => {
              const { data: docs } = await supabase
                .from('doctors')
                .select('*')
                .eq('institution_id', inst.id);
              return { institutionId: inst.id, doctors: (docs || []) as DoctorRow[] };
            }),
          );
          const map: Record<string, DoctorRow[]> = {};
          results.forEach((r) => { map[r.institutionId] = r.doctors; });
          setDoctorsMap(map);
        } else {
          setDoctorsMap({});
        }
      }
    } catch {
      setInstitutions([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadInstitutions(); }, [loadInstitutions]);

  useEffect(() => {
    const channel = supabase
      .channel('institutions-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'institutions' }, () => {
        loadInstitutions();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [loadInstitutions]);

  useEffect(() => {
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };

    const tc = i18n.getFixedT(i18n.language, 'common');
    const activeSvc = selectedServiceId
      ? treatmentServices.find((s) => s.id === selectedServiceId)
      : null;

    const parts: string[] = [];
    if (activeSvc) parts.push(t(activeSvc.titleKey));
    if (selectedCity) parts.push(selectedCity);
    if (selectedBranch) parts.push(selectedBranch);

    const baseTitle = tc('seo_institutions_title');
    const pageTitle = parts.length > 0
      ? `${parts.join(' \u00b7 ')} | ${baseTitle}`
      : baseTitle;

    const pageDesc = activeSvc
      ? `Find approved health institutions offering ${t(activeSvc.titleKey)}${selectedCity ? ` in ${selectedCity}` : ''} on Health Travel T\u00fcrkiye.`
      : selectedCity
        ? `Discover approved health institutions in ${selectedCity} on Health Travel T\u00fcrkiye.`
        : tc('seo_institutions_desc');

    document.title = pageTitle;
    setMeta('description', pageDesc);
    setMeta('og:title', pageTitle, true);
    setMeta('og:description', pageDesc, true);
    setMeta('og:image', 'https://public.readdy.ai/ai/img_res/fa0e434f01bb57024fb315f2867d6d5e.jpg', true);
    setMeta('og:image:width', '1200', true);
    setMeta('og:image:height', '630', true);
    setMeta('twitter:title', pageTitle);
    setMeta('twitter:description', pageDesc);
    setMeta('twitter:image', 'https://readdy.ai/api/search-image?query=Turkey%20accredited%20health%20institutions%20list%20modern%20medical%20clinics%20hospitals%20professional%20healthcare%20centers%20clean%20bright%20interiors&width=1200&height=630&seq=og-institutions-v1&orientation=landscape');
  }, [selectedCity, selectedBranch, selectedServiceId, t, i18n.language]);

  const allBranches = useMemo(() => {
    const set = new Set<string>();
    institutions.forEach((inst) => {
      (inst.departments || []).forEach((d) => set.add(d));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'tr'));
  }, [institutions]);

  const filteredBranches = branchSearch
    ? allBranches.filter((b) => b.toLowerCase().includes(branchSearch.toLowerCase()))
    : allBranches;

  const filteredCities = citySearch
    ? cities.filter((c) => c.toLowerCase().includes(citySearch.toLowerCase()))
    : cities;

  const filteredInstitutions = institutions
    .filter((inst) => {
      const matchesSearch =
        !searchQuery ||
        inst.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inst.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCity = !selectedCity || inst.city?.toLowerCase() === selectedCity.toLowerCase();
      const matchesBranch = !selectedBranch || (inst.departments || []).includes(selectedBranch);
      const matchesService =
        !activeServiceKeys ||
        (inst.services || []).some((s) => activeServiceKeys.includes(s));
      return matchesSearch && matchesCity && matchesBranch && matchesService;
    })
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name, 'tr');
      if (sortBy === 'city') return a.city.localeCompare(b.city, 'tr');
      if (a.is_pro_member && !b.is_pro_member) return -1;
      if (!a.is_pro_member && b.is_pro_member) return 1;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

  const updateParams = (updates: Record<string, string>) => {
    const params: Record<string, string> = {};
    if (searchQuery) params.q = searchQuery;
    if (selectedBranch) params.branch = selectedBranch;
    if (selectedCity) params.city = selectedCity;
    if (selectedServiceId) params.service = selectedServiceId;
    Object.assign(params, updates);
    Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
    setSearchParams(params);
  };

  const handleBranchSelect = (branch: string) => {
    const next = selectedBranch === branch ? '' : branch;
    setSelectedBranch(next);
    setBranchDropdownOpen(false);
    setBranchSearch('');
    updateParams({ branch: next });
  };

  const handleCitySelect = (city: string) => {
    const next = selectedCity === city ? '' : city;
    setSelectedCity(next);
    setCityDropdownOpen(false);
    setCitySearch('');
    updateParams({ city: next });
  };

  const clearFilters = () => {
    setSelectedBranch('');
    setSearchQuery('');
    setSelectedCity('');
    setSelectedServiceId('');
    setSearchParams({});
  };

  const hasActiveFilter = !!(selectedBranch || searchQuery || selectedCity || selectedServiceId);
  const activeFilterCount = [selectedBranch, selectedCity, searchQuery, selectedServiceId].filter(Boolean).length;

  const sortOptions = [
    { value: 'created_at', labelKey: 'inst_sort_newest', icon: 'ri-time-line' },
    { value: 'name', labelKey: 'inst_sort_name', icon: 'ri-sort-asc' },
    { value: 'city', labelKey: 'inst_sort_city', icon: 'ri-map-pin-line' },
  ];

  useSeoSchema(
    'schema-institutions',
    [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Türkiye Sağlık Kurumları',
        description: 'Compare approved health institutions across Turkey on Health Travel T\u00fcrkiye.',
        url: `${SITE_URL}/saglik-kurumlari`,
      },
    ],
    [{ nameKey: 'breadcrumb_institutions', url: `${SITE_URL}/saglik-kurumlari` }],
    i18n.language,
  );

  return (
    <div className="min-h-screen bg-gray-50 font-['Outfit']">
      <Navbar />

      <div style={{ paddingTop: 'var(--navbar-height, 160px)' }}>
        {/* Hero */}
        <div className="bg-gradient-to-br from-emerald-600 to-teal-600 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row items-center gap-10">
              <div className="flex-1 w-full">
                <h1 className="text-4xl font-bold text-white mb-3">{t('inst_page_title')}</h1>
                <p className="text-lg text-white/90 mb-8">
                  {t('inst_page_subtitle', { count: institutions.length })}
                </p>
                <div className="bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3">
                  <i className="ri-search-line text-xl text-gray-400 ml-1"></i>
                  <input
                    type="text"
                    placeholder={t('inst_search_placeholder')}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      updateParams({ q: e.target.value });
                    }}
                    className="flex-1 py-2 text-sm outline-none text-gray-700"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => { setSearchQuery(''); updateParams({ q: '' }); }}
                      className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-full hover:bg-gray-100"
                    >
                      <i className="ri-close-line text-sm"></i>
                    </button>
                  )}
                </div>
              </div>

              {/* Accreditation Logos */}
              <div className="hidden lg:flex flex-col gap-3 w-72 flex-shrink-0">
                <p className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-1">
                  {t('inst_official_accreditation')}
                </p>
                <div className="flex items-center gap-4 bg-white rounded-2xl px-4 py-3">
                  <div className="w-16 h-16 flex items-center justify-center flex-shrink-0">
                    <img
                      src="https://storage.readdy-site.link/project_files/b825b40c-ced1-45bc-acd7-e75b219f4e94/d3120357-b604-44a0-aefb-b4527d8be74a_Health-Turkiye-Logo.svg?v=733eda141137079f8cc11e4f506bec91"
                      alt="Health Türkiye"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 leading-tight">Health Türkiye</p>
                    <p className="text-xs text-gray-400 mt-0.5">Health Tourism Platform</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-white rounded-2xl px-4 py-3">
                  <div className="w-16 h-16 flex items-center justify-center flex-shrink-0">
                    <img
                      src="https://storage.readdy-site.link/project_files/b825b40c-ced1-45bc-acd7-e75b219f4e94/f60d29a1-c69a-4180-b9bf-f3bf3f7fe15d_Republic-of-Turkiye-Ministry-of-Health-Logo.png?v=71e00ccd8f6d40e8d4801bf4a26e4341"
                      alt="Ministry of Health"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 leading-tight">Republic of Turkey Ministry of Health</p>
                    <p className="text-xs text-gray-400 mt-0.5">Approved Health Institution</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-white rounded-2xl px-4 py-3">
                  <div className="w-16 h-16 flex items-center justify-center flex-shrink-0">
                    <img
                      src="https://storage.readdy-site.link/project_files/b825b40c-ced1-45bc-acd7-e75b219f4e94/d8a5e1d4-49c4-4acf-997a-18d6539223bc_Republic-of-Turkiye-Ministry-of-Cultureand-Tourism-Logo.png?v=1f9b9ce043aa351352a5d6d7c5120a9b"
                      alt="Ministry of Culture and Tourism"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 leading-tight">Ministry of Culture and Tourism</p>
                    <p className="text-xs text-gray-400 mt-0.5">Tourism Accreditation</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row gap-8">

            {/* Sidebar */}
            <aside className="w-full lg:w-72 flex-shrink-0">
              <div className="bg-white rounded-2xl p-5 sticky top-4 space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 rounded-lg">
                      <i className="ri-filter-3-line text-emerald-600 text-sm"></i>
                    </div>
                    <h2 className="text-sm font-bold text-gray-900">{t('inst_filters')}</h2>
                    {activeFilterCount > 0 && (
                      <span className="px-2 py-0.5 bg-emerald-600 text-white text-xs font-bold rounded-full">
                        {activeFilterCount}
                      </span>
                    )}
                  </div>
                  {hasActiveFilter && (
                    <button
                      onClick={clearFilters}
                      className="text-xs text-gray-400 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                    >
                      <i className="ri-close-line"></i>
                      {t('inst_remove')}
                    </button>
                  )}
                </div>

                {/* Branch Combobox */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    {t('inst_branch')}
                  </label>
                  <div ref={branchRef} className="relative">
                    <button
                      type="button"
                      onClick={() => { setBranchDropdownOpen(!branchDropdownOpen); setBranchSearch(''); }}
                      className={`w-full flex items-center justify-between px-3 py-2.5 border rounded-xl text-sm transition-colors cursor-pointer ${
                        branchDropdownOpen
                          ? 'border-emerald-500 bg-emerald-50'
                          : selectedBranch
                          ? 'border-emerald-300 bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <i className={`ri-hospital-line flex-shrink-0 ${selectedBranch ? 'text-emerald-600' : 'text-gray-400'}`}></i>
                        <span className={`truncate ${selectedBranch ? 'text-emerald-700 font-medium' : 'text-gray-500'}`}>
                          {selectedBranch || t('inst_all_branches')}
                        </span>
                      </span>
                      {branchDropdownOpen
                        ? <i className="ri-arrow-up-s-line text-gray-400 flex-shrink-0 ml-1"></i>
                        : <i className="ri-arrow-down-s-line text-gray-400 flex-shrink-0 ml-1"></i>
                      }
                    </button>

                    {branchDropdownOpen && (
                      <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl overflow-hidden">
                        <div className="p-2 border-b border-gray-100">
                          <div className="relative">
                            <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                            <input
                              type="text"
                              value={branchSearch}
                              onChange={(e) => setBranchSearch(e.target.value)}
                              placeholder={t('inst_branch_search')}
                              className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-lg outline-none text-xs focus:border-emerald-500"
                              autoFocus
                            />
                          </div>
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                          <button
                            type="button"
                            onClick={() => { setSelectedBranch(''); setBranchDropdownOpen(false); updateParams({ branch: '' }); }}
                            className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors cursor-pointer ${!selectedBranch ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-700'}`}
                          >
                            <i className="ri-apps-line text-gray-400 w-4 text-center"></i>
                            <span className="flex-1">{t('inst_all_branches')}</span>
                            {!selectedBranch && <i className="ri-check-line text-emerald-500 text-xs"></i>}
                          </button>
                          {allBranches.length === 0 && (
                            <p className="text-xs text-gray-400 text-center py-4">{t('inst_no_branches')}</p>
                          )}
                          {filteredBranches.map((branch) => (
                            <button
                              key={branch}
                              type="button"
                              onClick={() => handleBranchSelect(branch)}
                              className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors cursor-pointer ${selectedBranch === branch ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-700'}`}
                            >
                              <i className={`ri-hospital-line w-4 text-center flex-shrink-0 ${selectedBranch === branch ? 'text-emerald-600' : 'text-gray-400'}`}></i>
                              <span className="flex-1 truncate">{branch}</span>
                              {selectedBranch === branch && <i className="ri-check-line text-emerald-500 text-xs flex-shrink-0"></i>}
                            </button>
                          ))}
                          {filteredBranches.length === 0 && branchSearch && (
                            <p className="text-xs text-gray-400 text-center py-4">{t('inst_no_results_search')}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {selectedBranch && (
                    <div className="mt-2 flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <i className="ri-hospital-line text-emerald-600 text-xs"></i>
                      <span className="text-xs text-emerald-700 font-medium flex-1 truncate">{selectedBranch}</span>
                      <button
                        onClick={() => { setSelectedBranch(''); updateParams({ branch: '' }); }}
                        className="w-4 h-4 flex items-center justify-center text-emerald-400 hover:text-emerald-600 cursor-pointer flex-shrink-0"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </div>
                  )}
                </div>

                {/* City Combobox */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    {t('inst_city')}
                  </label>
                  <div ref={cityRef} className="relative">
                    <button
                      type="button"
                      onClick={() => { setCityDropdownOpen(!cityDropdownOpen); setCitySearch(''); }}
                      className={`w-full flex items-center justify-between px-3 py-2.5 border rounded-xl text-sm transition-colors cursor-pointer ${
                        cityDropdownOpen
                          ? 'border-emerald-500 bg-emerald-50'
                          : selectedCity
                          ? 'border-emerald-300 bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <i className={`ri-map-pin-line flex-shrink-0 ${selectedCity ? 'text-emerald-600' : 'text-gray-400'}`}></i>
                        <span className={selectedCity ? 'text-emerald-700 font-medium' : 'text-gray-500'}>
                          {selectedCity || t('inst_all_cities')}
                        </span>
                      </span>
                      {cityDropdownOpen
                        ? <i className="ri-arrow-up-s-line text-gray-400 flex-shrink-0"></i>
                        : <i className="ri-arrow-down-s-line text-gray-400 flex-shrink-0"></i>
                      }
                    </button>

                    {cityDropdownOpen && (
                      <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl overflow-hidden">
                        <div className="p-2 border-b border-gray-100">
                          <div className="relative">
                            <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                            <input
                              type="text"
                              value={citySearch}
                              onChange={(e) => setCitySearch(e.target.value)}
                              placeholder={t('inst_city_search')}
                              className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-lg outline-none text-xs focus:border-emerald-500"
                              autoFocus
                            />
                          </div>
                        </div>
                        <div className="max-h-52 overflow-y-auto">
                          <button
                            type="button"
                            onClick={() => { setSelectedCity(''); setCityDropdownOpen(false); updateParams({ city: '' }); }}
                            className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors cursor-pointer ${!selectedCity ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-700'}`}
                          >
                            <i className="ri-map-pin-line text-gray-400 w-4 text-center"></i>
                            <span className="flex-1">{t('inst_all_cities')}</span>
                            {!selectedCity && <i className="ri-check-line text-emerald-500 text-xs"></i>}
                          </button>
                          {filteredCities.map((city) => (
                            <button
                              key={city}
                              type="button"
                              onClick={() => handleCitySelect(city)}
                              className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors cursor-pointer ${selectedCity === city ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-700'}`}
                            >
                              <i className={`ri-map-pin-line w-4 text-center flex-shrink-0 ${selectedCity === city ? 'text-emerald-600' : 'text-gray-400'}`}></i>
                              <span className="flex-1">{city}</span>
                              {selectedCity === city && <i className="ri-check-line text-emerald-500 text-xs flex-shrink-0"></i>}
                            </button>
                          ))}
                          {filteredCities.length === 0 && (
                            <p className="text-xs text-gray-400 text-center py-4">{t('inst_no_results_search')}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sort */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    {t('inst_sort')}
                  </label>
                  <div className="space-y-1">
                    {sortOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setSortBy(opt.value)}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                          sortBy === opt.value
                            ? 'bg-emerald-50 text-emerald-700 font-medium'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <i className={`${opt.icon} w-4 text-center flex-shrink-0 ${sortBy === opt.value ? 'text-emerald-600' : 'text-gray-400'}`}></i>
                        <span>{t(opt.labelKey)}</span>
                        {sortBy === opt.value && <i className="ri-check-line text-emerald-500 text-xs ml-auto"></i>}
                      </button>
                    ))}
                  </div>
                </div>

                {selectedServiceId && (() => {
                  const svc = treatmentServices.find((s) => s.id === selectedServiceId);
                  return svc ? (
                    <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl">
                      <p className="text-[10px] font-semibold text-rose-400 uppercase tracking-wider mb-1.5">{t('inst_active_service_filter')}</p>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-rose-500 flex-shrink-0">
                          <i className={`${svc.icon} text-white text-xs`}></i>
                        </div>
                        <span className="text-xs font-semibold text-rose-700 flex-1 truncate">{t(svc.titleKey)}</span>
                        <button
                          onClick={() => { setSelectedServiceId(''); updateParams({ service: '' }); }}
                          className="w-5 h-5 flex items-center justify-center text-rose-400 hover:text-rose-600 cursor-pointer flex-shrink-0"
                        >
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      </div>
                    </div>
                  ) : null;
                })()}

                {hasActiveFilter && (
                  <button
                    onClick={clearFilters}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-red-200 text-red-500 rounded-xl text-sm font-medium hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-filter-off-line"></i>
                    {t('inst_clear_filters')}
                  </button>
                )}
              </div>
            </aside>

            {/* Institution List */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-3">
                <div>
                  <p className="text-sm text-gray-600">
                    <strong className="text-gray-900">{filteredInstitutions.length}</strong> {t('inst_results', { count: filteredInstitutions.length }).replace(`${filteredInstitutions.length} `, '')}
                    {selectedBranch && (
                      <span className="ml-2 text-emerald-600 font-medium">· {selectedBranch}</span>
                    )}
                    {selectedCity && (
                      <span className="ml-2 text-emerald-600 font-medium">· {selectedCity}</span>
                    )}
                  </p>
                  {hasActiveFilter && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {t('inst_filtered_from', { total: institutions.length })}
                    </p>
                  )}
                </div>
              </div>

              {selectedServiceId && (() => {
                const svc = treatmentServices.find((s) => s.id === selectedServiceId);
                return svc ? (
                  <div className="mb-5 p-4 rounded-2xl border border-rose-200 bg-rose-50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-rose-500 shadow">
                        <i className={`${svc.icon} text-white text-base`}></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">{t(svc.titleKey)}</p>
                        <p className="text-xs text-gray-500">{t(svc.subtitleKey)}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => { setSelectedServiceId(''); updateParams({ service: '' }); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white/80 text-gray-600 rounded-lg text-xs font-medium hover:bg-white transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-close-line"></i>
                      {t('inst_remove')}
                    </button>
                  </div>
                ) : null;
              })()}

              {selectedBranch && (
                <div className="mb-5 p-4 rounded-2xl border border-emerald-200 bg-emerald-50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-600 shadow">
                      <i className="ri-hospital-line text-white text-base"></i>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">{selectedBranch}</p>
                      <p className="text-xs text-gray-500">{t('inst_branch_active')}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setSelectedBranch(''); updateParams({ branch: '' }); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/80 text-gray-600 rounded-lg text-xs font-medium hover:bg-white transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-close-line"></i>
                    {t('inst_remove')}
                  </button>
                </div>
              )}

              {loading ? (
                <div className="text-center py-20">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
                  <p className="mt-4 text-gray-600">{t('inst_loading')}</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-6">
                    {filteredInstitutions.map((institution) => {
                      const doctors = doctorsMap[institution.id] || [];
                      return (
                        <Link
                          key={institution.id}
                          to={`/kurum/${institution.id}`}
                          className="group bg-white rounded-2xl overflow-hidden hover:ring-2 hover:ring-emerald-200 transition-all duration-300 hover:-translate-y-0.5"
                        >
                          <div className="relative h-48 overflow-hidden">
                            <img
                              src={
                                institution.cover_image_url ||
                                'https://readdy.ai/api/search-image?query=modern%20medical%20clinic%20interior%2C%20bright%20and%20clean%20healthcare%20facility%2C%20professional%20hospital%20environment%2C%20high%20quality%20medical%20center&width=400&height=300&seq=inst-cover&orientation=landscape'
                              }
                              alt={institution.name}
                              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            />
                            {institution.logo_url && (
                              <div className="absolute top-3 left-3 bg-white p-1.5 rounded-lg">
                                <img src={institution.logo_url} alt="Logo" className="w-10 h-10 object-contain" />
                              </div>
                            )}
                            {institution.is_pro_member && (
                              <div className="absolute top-3 right-3 px-2.5 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                                <i className="ri-vip-crown-line"></i>
                                PRO
                              </div>
                            )}
                          </div>

                          <div className="p-5">
                            {(institution.departments || []).length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-3">
                                {(institution.departments || []).slice(0, 3).map((dep) => (
                                  <span
                                    key={dep}
                                    className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                                      selectedBranch === dep
                                        ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300'
                                        : 'bg-gray-100 text-gray-500'
                                    }`}
                                  >
                                    {dep}
                                  </span>
                                ))}
                                {(institution.departments || []).length > 3 && (
                                  <span className="px-2 py-0.5 bg-gray-100 text-gray-400 text-[10px] font-medium rounded-full">
                                    +{(institution.departments || []).length - 3}
                                  </span>
                                )}
                              </div>
                            )}

                            <div className="flex items-center gap-1.5 mb-2">
                              <i className="ri-map-pin-line text-gray-400 text-xs"></i>
                              <span className="text-xs text-gray-500">
                                {institution.city}{institution.district ? `, ${institution.district}` : ''}
                              </span>
                            </div>

                            <h3 className="text-base font-bold text-gray-900 mb-1.5 group-hover:text-emerald-600 transition-colors line-clamp-1">
                              {institution.name}
                            </h3>
                            <p className="text-xs text-gray-500 mb-3 line-clamp-2">
                              {institution.description || 'Professional health services'}
                            </p>

                            <div className="flex items-center justify-between">
                              {doctors.length > 0 ? (
                                <span className="flex items-center gap-1 text-xs text-gray-500">
                                  <i className="ri-user-heart-line text-emerald-500"></i>
                                  {t('inst_expert_doctors', { count: doctors.length })}
                                </span>
                              ) : (
                                <span></span>
                              )}
                              <span className="text-xs font-semibold text-emerald-600 flex items-center gap-1 whitespace-nowrap">
                                {t('inst_view_details')}
                                <i className="ri-arrow-right-line"></i>
                              </span>
                            </div>

                            {institution.is_pro_member && (
                              <div className="mt-3 pt-3 border-t border-gray-100">
                                <div className="flex items-center gap-1.5 text-xs text-amber-600 font-medium">
                                  <i className="ri-vip-crown-line"></i>
                                  <span>{t('inst_consultation_available')}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </Link>
                      );
                    })}
                  </div>

                  {filteredInstitutions.length === 0 && (
                    <div className="text-center py-20">
                      <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-5">
                        <i className="ri-search-line text-4xl text-gray-300"></i>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('inst_no_results_title')}</h3>
                      <p className="text-gray-500 mb-6 text-sm">
                        {selectedBranch
                          ? t('inst_no_results_branch', { branch: selectedBranch })
                          : t('inst_no_results_desc')}
                      </p>
                      {hasActiveFilter && (
                        <button
                          onClick={clearFilters}
                          className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          {t('inst_clear_filters_btn')}
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
